import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  CheckCircle2,
  Download as DownloadIcon,
  ImagePlus,
  Library,
  Loader2,
  RefreshCw,
  Save,
  Scissors,
  SlidersHorizontal,
  Clapperboard,
} from 'lucide-react';
import { GeneratedMockup, GenerateMockupRequest, mockupService } from '../services/mockupService';
import { api } from '../services/api';
import { downloadService } from '../services/downloadService';
import { libraryService } from '../services/libraryService';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../utils/apiUrl';
import ImagePreviewModal from '../components/ImagePreviewModal';
import StepProgress from '../components/StepProgress';
import MockupFlowSummary from '../components/MockupFlowSummary';
import { formatTemplateName, getPrimarySourceLabel } from '../utils/mockupFlow';

type BannerState = {
  type: 'error' | 'success';
  text: string;
} | null;

const Download: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mockup, setMockup] = useState<GeneratedMockup | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [banner, setBanner] = useState<BannerState>(null);
  const [selectedFormat, setSelectedFormat] = useState<'png' | 'jpg'>('png');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [backgroundRemovedMockup, setBackgroundRemovedMockup] = useState<GeneratedMockup | null>(null);
  const [previewTarget, setPreviewTarget] = useState<'current' | 'transparent' | null>(null);

  const resolveMockupUrl = (url?: string) => {
    if (!url) {
      return '';
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return `${API_BASE_URL}${url}`;
  };

  const templateLabel = useMemo(() => {
    if (!mockup?.template) {
      return 'Mockup';
    }

    return mockup.template
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [mockup?.template]);

  const previewMockup = previewTarget === 'transparent' && backgroundRemovedMockup ? backgroundRemovedMockup : mockup;
  const generationSettings = useMemo(() => {
    const rawOptions = sessionStorage.getItem('lastGenerateOptions');
    if (!rawOptions) {
      return null;
    }

    try {
      return JSON.parse(rawOptions) as GenerateMockupRequest;
    } catch (parseError) {
      console.error('Failed to parse generation settings summary:', parseError);
      return null;
    }
  }, [mockup?.id]);

  useEffect(() => {
    sessionStorage.setItem('creationStep', '4');
    const storedBackgroundRemoved = sessionStorage.getItem('backgroundRemovedMockup');
    if (storedBackgroundRemoved) {
      try {
        setBackgroundRemovedMockup(JSON.parse(storedBackgroundRemoved) as GeneratedMockup);
      } catch (parseError) {
        console.error('Failed to parse background removed mockup payload:', parseError);
        sessionStorage.removeItem('backgroundRemovedMockup');
      }
    }

    const storedMockup = sessionStorage.getItem('generatedMockup');
    const mockupId = sessionStorage.getItem('generatedMockupId');

    if (storedMockup) {
      try {
        setMockup(JSON.parse(storedMockup) as GeneratedMockup);
        setIsLoading(false);
        return;
      } catch (parseError) {
        console.error('Failed to parse stored mockup payload:', parseError);
        sessionStorage.removeItem('generatedMockup');
      }
    }

    if (!mockupId) {
      setPageError('No mockup found. Generate a mockup first.');
      setIsLoading(false);
      return;
    }

    const fetchMockup = async () => {
      try {
        const mockupInfo = await mockupService.getMockupInfo(mockupId);
        persistGeneratedMockup(mockupInfo);
      } catch (error) {
        console.error(error);
        setPageError('Failed to load your mockup. It may have expired or an error occurred.');
      } finally {
        setIsLoading(false);
      }
    };

    void fetchMockup();
  }, []);

  const persistGeneratedMockup = (nextMockup: GeneratedMockup) => {
    setMockup(nextMockup);
    sessionStorage.setItem('generatedMockupId', nextMockup.id);
    sessionStorage.setItem('generatedMockup', JSON.stringify(nextMockup));
  };

  const clearGeneratedArtifacts = () => {
    setBackgroundRemovedMockup(null);
    sessionStorage.removeItem('backgroundRemovedMockup');
  };

  const getStoredGenerateOptions = (): GenerateMockupRequest | null => {
    const rawOptions = sessionStorage.getItem('lastGenerateOptions');
    if (!rawOptions) {
      return null;
    }

    try {
      return JSON.parse(rawOptions) as GenerateMockupRequest;
    } catch (parseError) {
      console.error('Failed to parse saved generation payload:', parseError);
      sessionStorage.removeItem('lastGenerateOptions');
      return null;
    }
  };

  const steps = [
    { number: 1, title: 'Choose Template', description: 'Select mockup type' },
    { number: 2, title: 'Upload Assets', description: 'Add your images' },
    { number: 3, title: 'Customize', description: 'Adjust settings' },
    { number: 4, title: 'Ready', description: 'Review and export' },
  ];

  const enabledEffectsCount = [
    generationSettings?.showShadow,
    generationSettings?.showReflection,
    generationSettings?.glossyFinish,
    generationSettings?.embossEffect,
    generationSettings?.gradientOverlay,
  ].filter(Boolean).length;

  const generationSummaryItems = [
    { label: 'Template', value: formatTemplateName(sessionStorage.getItem('selectedTemplate') || mockup?.template), tone: 'default' as const },
    { label: 'Primary Source', value: getPrimarySourceLabel(), tone: 'success' as const },
    { label: 'Creative Direction', value: generationSettings?.customPrompt ? 'Custom prompt applied' : 'No custom prompt', tone: generationSettings?.customPrompt ? 'success' as const : 'default' as const },
    { label: 'Output Status', value: backgroundRemovedMockup ? 'Original + transparent ready' : isSaved ? 'Saved to library' : 'Ready to export', tone: 'success' as const },
  ];

  const handleDownload = async (target: 'current' | 'transparent' = 'current') => {
    if (!mockup) {
      return;
    }

    const activeMockup = target === 'transparent' && backgroundRemovedMockup ? backgroundRemovedMockup : mockup;
    const format = target === 'transparent' ? 'png' : selectedFormat;

    setIsDownloading(true);
    setBanner(null);

    try {
      await downloadService.downloadMockup(
        activeMockup.id,
        activeMockup.filename,
        format,
        'web',
        activeMockup.url
      );
    } catch (error: any) {
      const message = error.message || 'Download failed.';
      setBanner({
        type: 'error',
        text: message.includes('404') || message.includes('not found') || message.includes('expired')
          ? 'This mockup has expired. Regenerate a new variation from the saved settings.'
          : message,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRegenerateVariation = async () => {
    const generateOptions = getStoredGenerateOptions();
    if (!generateOptions) {
      setBanner({
        type: 'error',
        text: 'We could not find the original generation settings. Use Adjust Settings to generate a new mockup.',
      });
      return;
    }

    setIsRegenerating(true);
    setBanner(null);

    try {
      const response = await mockupService.generateMockup(generateOptions);
      if (!response.success || !response.mockups?.length) {
        throw new Error(response.error || 'Failed to regenerate variation');
      }

      clearGeneratedArtifacts();
      setIsSaved(false);
      persistGeneratedMockup(response.mockups[0]);
      sessionStorage.setItem('creationStep', '3');
      setBanner({
        type: 'success',
        text: 'New variation generated with the same settings.',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      console.error('Error regenerating mockup:', error);
      setBanner({
        type: 'error',
        text: error.message || 'Failed to regenerate variation.',
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleChooseAnotherTemplate = () => {
    const mockupKeys = [
      'selectedTemplate',
      'primaryImageId',
      'primaryImageUrl',
      'secondaryImageId',
      'secondaryImageUrl',
      'useBrandLogo',
      'isAssetLibrary',
      'brandColors',
      'creationStep',
      'generatedMockupId',
      'generatedMockup',
      'backgroundRemovedMockup',
      'lastGenerateOptions',
    ];

    mockupKeys.forEach((key) => sessionStorage.removeItem(key));
    navigate('/dashboard/mockups');
  };

  const handleAdjustSettings = () => {
    navigate('/dashboard/customize');
  };

  const handleSaveToLibrary = async () => {
    if (!mockup || !user) {
      return;
    }

    setIsSaving(true);
    setBanner(null);

    try {
      const response = await api.get(`/api/download/${mockup.id}/png`, {
        params: { resolution: 'web', sourceUrl: mockup.url },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'image/png' });
      if (!blob || blob.size === 0) {
        throw new Error('Invalid mockup file');
      }

      const templateName = sessionStorage.getItem('selectedTemplate') || mockup.template;
      const settings = (mockup as any).settings || {};

      const result = await libraryService.saveMockup({
        name: `${templateName.replace(/-/g, ' ')} - ${new Date().toLocaleDateString()}`,
        templateId: mockup.template,
        templateName,
        mockupFile: blob,
        settings,
        width: 1600,
        height: 1200,
      });

      if (!result.mockup) {
        throw new Error(result.error || 'Failed to save mockup');
      }

      setIsSaved(true);
      setBanner({
        type: 'success',
        text: 'Saved to Library.',
      });
    } catch (error: any) {
      console.error('Error saving to library:', error);
      setBanner({
        type: 'error',
        text: error.message || 'Failed to save mockup to library.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!mockup) {
      return;
    }

    setIsRemovingBackground(true);
    setBanner(null);

    try {
      const response = await mockupService.removeBackground({
        mockupId: mockup.id,
        mockupUrl: mockup.url,
        template: mockup.template,
      });

      if (!response) {
        throw new Error('Failed to remove background');
      }

      setBackgroundRemovedMockup(response);
      sessionStorage.setItem('backgroundRemovedMockup', JSON.stringify(response));
      setBanner({
        type: 'success',
        text: 'Transparent PNG created.',
      });
    } catch (error: any) {
      console.error('Error removing background:', error);
      setBanner({
        type: 'error',
        text: error.message || 'Failed to remove background.',
      });
    } finally {
      setIsRemovingBackground(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl items-center justify-center px-6 py-12">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary-600" />
          <p className="mt-4 text-gray-600">Loading your generated mockup...</p>
        </div>
      </div>
    );
  }

  if (pageError || !mockup) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
          <h2 className="text-xl font-semibold text-red-800">Unable to Load Mockup</h2>
          <p className="mt-2 text-red-700">{pageError || 'No mockup available.'}</p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={() => navigate('/dashboard/mockups')}
              className="rounded-lg bg-primary-600 px-5 py-2.5 font-medium text-white transition-colors hover:bg-primary-700"
            >
              Back to Templates
            </button>
            <button
              onClick={() => navigate('/dashboard/customize')}
              className="rounded-lg border border-red-300 px-5 py-2.5 font-medium text-red-700 transition-colors hover:bg-red-100"
            >
              Adjust Settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <StepProgress
        currentStep={4}
        steps={steps}
        helperText="Your generation is complete. Review the output, save it, or branch into a new variation from the same setup."
      />

      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="mb-3 text-4xl font-bold text-gray-900">Your Mockup Is Ready</h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            Download it, generate a fresh variation with the same settings, or adjust the setup before trying again.
          </p>
        </div>

        <div className="mb-8">
          <MockupFlowSummary
            title="Generation Summary"
            description="The final screen now keeps the generation context visible so the user can understand what was used without jumping back."
            items={generationSummaryItems}
          />
        </div>

        {banner && (
          <div
            className={`mb-6 flex items-center justify-between rounded-xl border px-4 py-3 ${
              banner.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            <p className="text-sm font-medium">{banner.text}</p>
            <button
              onClick={() => setBanner(null)}
              className={`text-sm font-semibold ${
                banner.type === 'success' ? 'text-green-700 hover:text-green-900' : 'text-red-700 hover:text-red-900'
              }`}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.4fr)_380px]">
        <div className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Current Mockup</h2>
                <p className="text-sm text-gray-500">{templateLabel}</p>
              </div>
              <div className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
                {selectedFormat}
              </div>
            </div>
            <div className="bg-gray-50 p-6">
              <button
                type="button"
                onClick={() => setPreviewTarget('current')}
                className="group relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-2xl bg-white"
              >
                <img
                  src={resolveMockupUrl(mockup.url)}
                  alt="Generated mockup"
                  className="h-full w-full object-contain transition-transform duration-200 group-hover:scale-[1.01]"
                />
                <div className="pointer-events-none absolute inset-x-4 bottom-4 rounded-xl bg-black/55 px-4 py-2 text-sm font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                  Click to preview and zoom
                </div>
              </button>
            </div>
          </div>

          {backgroundRemovedMockup && (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Transparent Version</h2>
                  <p className="text-sm text-gray-500">Background removed and ready for compositing.</p>
                </div>
                <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  PNG
                </div>
              </div>
              <div className="p-6">
                <button
                  type="button"
                  onClick={() => setPreviewTarget('transparent')}
                  className="group relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-2xl"
                  style={{
                    backgroundImage:
                      'linear-gradient(45deg, #d1d5db 25%, transparent 25%), linear-gradient(-45deg, #d1d5db 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d1d5db 75%), linear-gradient(-45deg, transparent 75%, #d1d5db 75%)',
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0',
                  }}
                >
                  <img
                    src={resolveMockupUrl(backgroundRemovedMockup.url)}
                    alt="Background removed mockup"
                    className="relative z-10 h-full w-full object-contain transition-transform duration-200 group-hover:scale-[1.01]"
                  />
                  <div className="pointer-events-none absolute inset-x-4 bottom-4 z-20 rounded-xl bg-black/55 px-4 py-2 text-sm font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                    Click to preview and zoom
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6 lg:sticky lg:top-8 lg:self-start">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Download</h2>
            <p className="mt-2 text-sm text-gray-600">
              Choose your export format. Downloads now use the in-app file save flow directly.
            </p>

            <div className="mt-5">
              <p className="mb-3 text-sm font-medium text-gray-700">Format</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSelectedFormat('png')}
                  className={`rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                    selectedFormat === 'png'
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  PNG
                </button>
                <button
                  onClick={() => setSelectedFormat('jpg')}
                  className={`rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                    selectedFormat === 'jpg'
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  JPG
                </button>
              </div>
            </div>

            {selectedFormat === 'jpg' && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                JPG replaces transparency with a white background.
              </div>
            )}

            <div className="mt-6 space-y-3">
              <button
                onClick={() => handleDownload('current')}
                disabled={isDownloading || isRegenerating}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-3.5 font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {isDownloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <DownloadIcon className="h-5 w-5" />}
                {isDownloading ? 'Downloading...' : `Download ${selectedFormat.toUpperCase()}`}
              </button>

              {backgroundRemovedMockup && (
                <button
                  onClick={() => handleDownload('transparent')}
                  disabled={isDownloading || isRegenerating}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-5 py-3 font-semibold text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <DownloadIcon className="h-5 w-5" />
                  Download Transparent PNG
                </button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Next Actions</h2>
            <div className="mt-5 space-y-3">
              <button
                onClick={handleRegenerateVariation}
                disabled={isRegenerating || isDownloading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-3.5 font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {isRegenerating ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
                {isRegenerating ? 'Generating Variation...' : 'Regenerate Variations'}
              </button>

              <button
                onClick={handleAdjustSettings}
                disabled={isRegenerating}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 px-5 py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SlidersHorizontal className="h-5 w-5" />
                Adjust Settings
              </button>

              {mockup && (
                <button
                  onClick={() => navigate(`/dashboard/video-mockups?source=${mockup.id}`)}
                  disabled={isRegenerating}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-5 py-3 font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Clapperboard className="h-5 w-5" />
                  Turn Into Video
                </button>
              )}

              {!backgroundRemovedMockup && (
                <button
                  onClick={handleRemoveBackground}
                  disabled={isRemovingBackground || isRegenerating}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-purple-200 bg-purple-50 px-5 py-3 font-semibold text-purple-700 transition-colors hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isRemovingBackground ? <Loader2 className="h-5 w-5 animate-spin" /> : <Scissors className="h-5 w-5" />}
                  {isRemovingBackground ? 'Removing Background...' : 'Create Transparent PNG'}
                </button>
              )}

              {user && !isSaved ? (
                <button
                  onClick={handleSaveToLibrary}
                  disabled={isSaving || isRegenerating}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-brand-purple/20 bg-brand-purple/5 px-5 py-3 font-semibold text-brand-purple transition-colors hover:bg-brand-purple/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                  {isSaving ? 'Saving...' : 'Save to Library'}
                </button>
              ) : (
                <button
                  onClick={() => navigate('/dashboard/library')}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-green-200 bg-green-50 px-5 py-3 font-semibold text-green-700 transition-colors hover:bg-green-100"
                >
                  {isSaved ? <Check className="h-5 w-5" /> : <Library className="h-5 w-5" />}
                  {isSaved ? 'Saved to Library' : 'Open Library'}
                </button>
              )}

              <button
                onClick={handleChooseAnotherTemplate}
                disabled={isRegenerating}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 px-5 py-3 font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ImagePlus className="h-5 w-5" />
                Choose Another Template
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Recommended Next Steps</h3>
            <div className="mt-4 space-y-3 text-sm text-gray-600">
              <p>{enabledEffectsCount ? `${enabledEffectsCount} visual effects were active in this generation.` : 'This variation was generated without extra visual effects.'}</p>
              <p>Regenerate Variations keeps the same generation settings and asks the API for a fresh result.</p>
              <p>Use Adjust Settings when you want to change colors, prompts, effects, or composition before regenerating.</p>
              <p>Transparent PNG is useful for presentations, layered design work, and external compositing.</p>
            </div>
          </div>
        </div>
      </div>

        {previewMockup && previewTarget && (
          <ImagePreviewModal
            src={resolveMockupUrl(previewMockup.url)}
            alt={previewTarget === 'transparent' ? 'Transparent mockup preview' : 'Generated mockup preview'}
            title={previewTarget === 'transparent' ? `${templateLabel} Transparent Version` : templateLabel}
            subtitle={previewTarget === 'transparent' ? 'Background removed preview' : 'Generated mockup preview'}
            onClose={() => setPreviewTarget(null)}
            onDownload={() => {
              void handleDownload(previewTarget);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Download;
